import {Plus, Trash2} from 'lucide-react'
import {useI18n} from '@/i18n'
import {useAppConfig, type LocalizedTypeValue} from '@/hooks/use-app-config'
import {CertificationConditionField} from '@/types/certification'
import {ConditionOperator} from '@/types/approval-policy'
import {AssignmentSourceType} from '@/types/assignment'
import {Button} from '@/components/ui/button'
import {Badge} from '@/components/ui/badge'
import {Label} from '@/components/ui/label'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {ContentArea} from '@/components/shared/ContentArea'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {Icon} from '@/components/shared/Icon'
import type {ScopeConditionFormEntry} from '@/features/certification/hooks/useCertificationPolicyWizardForm'

// WHY: SOURCE_TYPE is unsupported in conditions mode — the SQL path returns 400
// (CE0015), so it is hidden from the field selector (spec §3.4 / 949.01).
const CONDITION_FIELDS = Object.values(CertificationConditionField)
    .filter((f) => f !== CertificationConditionField.SOURCE_TYPE)
const CONDITION_OPERATORS: ConditionOperator[] = Object.values(ConditionOperator)
const SOURCE_TYPE_VALUES = Object.values(AssignmentSourceType)

function getValuesForField(
    field: string,
    appConfig: {riskLevelTypes: LocalizedTypeValue[], resourceTypes: LocalizedTypeValue[], environmentTypes: LocalizedTypeValue[]},
    i18n: ReturnType<typeof useI18n>,
): {code: string, label: string}[] {
    switch (field) {
        case CertificationConditionField.RISK_LEVEL:
            return appConfig.riskLevelTypes.map((tv) => ({code: tv.code, label: tv.label}))
        case CertificationConditionField.RESOURCE_TYPE:
            return appConfig.resourceTypes.map((tv) => ({code: tv.code, label: tv.label}))
        case CertificationConditionField.ENVIRONMENT:
            return appConfig.environmentTypes.map((tv) => ({code: tv.code, label: tv.label}))
        case CertificationConditionField.SOURCE_TYPE:
            return SOURCE_TYPE_VALUES.map((code) => ({
                code,
                label: i18n.certificationPolicies.sourceTypeLabels[code] ?? code,
            }))
        default:
            return []
    }
}

export interface ConditionsEditorProps {
    conditions: ScopeConditionFormEntry[]
    onChange: (conditions: ScopeConditionFormEntry[]) => void
    disabled?: boolean
}

/**
 * Structured-conditions scope editor extracted from the legacy
 * CertificationPolicyDetail flat form. Field / operator / value rows with an
 * IN-multiselect of clickable Badge chips, used-field+operator-pair de-dup,
 * per-field value source, and add/remove. `SOURCE_TYPE` is hidden from the
 * field selector (un-previewable + un-generatable in conditions mode).
 */
export function ConditionsEditor({conditions, onChange, disabled = false}: ConditionsEditorProps) {
    const i18n = useI18n()
    const appConfig = useAppConfig()

    const usedFieldOperatorPairs = new Set(conditions.map((c) => `${c.field}:${c.operator}`))
    const allPairsUsed = usedFieldOperatorPairs.size >= CONDITION_FIELDS.length * CONDITION_OPERATORS.length

    const addCondition = () => {
        let nextField: string = CONDITION_FIELDS[0]
        let nextOperator: ConditionOperator = ConditionOperator.EQUALS
        for (const f of CONDITION_FIELDS) {
            for (const op of CONDITION_OPERATORS) {
                if (!usedFieldOperatorPairs.has(`${f}:${op}`)) {
                    nextField = f
                    nextOperator = op
                    break
                }
            }
            if (!usedFieldOperatorPairs.has(`${nextField}:${nextOperator}`)) break
        }
        onChange([...conditions, {field: nextField, operator: nextOperator, value: ''}])
    }

    const removeCondition = (index: number) => {
        onChange(conditions.filter((_, i) => i !== index))
    }

    const updateCondition = (index: number, updates: Partial<ScopeConditionFormEntry>) => {
        onChange(conditions.map((c, i) => {
            if (i !== index) return c
            if (updates.field && updates.field !== c.field) {
                return {...c, ...updates, value: ''}
            }
            return {...c, ...updates}
        }))
    }

    const toggleInValue = (index: number, code: string) => {
        onChange(conditions.map((c, i) => {
            if (i !== index) return c
            const current = c.value ? c.value.split(',') : []
            const next = current.includes(code) ? current.filter((v) => v !== code) : [...current, code]
            return {...c, value: next.join(',')}
        }))
    }

    return (
        <Stack gap="md">
            {conditions.length === 0 ? (
                <InfoBanner variant="info" data-testid="conditions-empty-info">
                    {i18n.certificationPolicies.wizard.noConditionsInfo}
                </InfoBanner>
            ) : (
                conditions.map((condition, index) => (
                    <InlineRow key={index} align="end" gap="md">
                        <ContentArea>
                            {index === 0 && (
                                <Label className="text-xs">{i18n.certificationPolicies.conditionField}</Label>
                            )}
                            <Select
                                value={condition.field}
                                disabled={disabled}
                                onValueChange={(v) => updateCondition(index, {field: v})}
                            >
                                <SelectTrigger data-testid={`select-condition-field-${index}`}>
                                    <SelectValue/>
                                </SelectTrigger>
                                <SelectContent>
                                    {CONDITION_FIELDS.map((f) => (
                                        <SelectItem key={f} value={f} data-testid={`option-condition-field-${f}`}>
                                            {i18n.certificationPolicies.conditionFieldLabels[f]}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </ContentArea>
                        <Stack className="w-40">
                            {index === 0 && (
                                <Label className="text-xs">{i18n.certificationPolicies.conditionOperator}</Label>
                            )}
                            <Select
                                value={condition.operator}
                                disabled={disabled}
                                onValueChange={(v) => updateCondition(index, {operator: v})}
                            >
                                <SelectTrigger data-testid={`select-condition-operator-${index}`}>
                                    <SelectValue/>
                                </SelectTrigger>
                                <SelectContent>
                                    {CONDITION_OPERATORS.filter((op) =>
                                        op === condition.operator || !usedFieldOperatorPairs.has(`${condition.field}:${op}`),
                                    ).map((op) => (
                                        <SelectItem key={op} value={op} data-testid={`option-condition-operator-${op}`}>
                                            {i18n.certificationPolicies.conditionOperatorLabels[op]}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Stack>
                        <ContentArea>
                            {index === 0 && (
                                <Label className="text-xs">{i18n.certificationPolicies.conditionValue}</Label>
                            )}
                            {condition.operator === ConditionOperator.IN ? (
                                <InlineRow wrap gap="xs" className="min-h-[36px]" data-testid={`badges-condition-value-${index}`}>
                                    {getValuesForField(condition.field, appConfig, i18n).map((opt) => {
                                        const selected = condition.value.split(',').includes(opt.code)
                                        return (
                                            <Badge
                                                key={opt.code}
                                                variant={selected ? 'default' : 'outline'}
                                                className="cursor-pointer select-none"
                                                onClick={() => { if (!disabled) toggleInValue(index, opt.code) }}
                                                data-testid={`badge-${opt.code}`}
                                            >
                                                {opt.label}
                                            </Badge>
                                        )
                                    })}
                                </InlineRow>
                            ) : (
                                <Select
                                    value={condition.value}
                                    disabled={disabled}
                                    onValueChange={(v) => updateCondition(index, {value: v})}
                                >
                                    <SelectTrigger data-testid={`select-condition-value-${index}`}>
                                        <SelectValue placeholder={i18n.certificationPolicies.conditionValuePlaceholder}/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {getValuesForField(condition.field, appConfig, i18n).map((opt) => (
                                            <SelectItem key={opt.code} value={opt.code} data-testid={`option-condition-value-${opt.code}`}>
                                                {opt.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </ContentArea>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            disabled={disabled}
                            onClick={() => removeCondition(index)}
                            className="shrink-0"
                            aria-label={i18n.certificationPolicies.removeCondition}
                            data-testid={`btn-remove-condition-${index}`}
                        >
                            <Icon icon={Trash2} className="text-destructive"/>
                        </Button>
                    </InlineRow>
                ))
            )}
            <InlineRow>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addCondition}
                    disabled={allPairsUsed || disabled}
                    data-testid="btn-add-condition"
                >
                    <Icon icon={Plus} size="xs" className="mr-1"/>
                    {i18n.certificationPolicies.addCondition}
                </Button>
            </InlineRow>
            {conditions.length === 0 && (
                <InlineEmptyText data-testid="conditions-empty-hint">
                    {i18n.certificationPolicies.noConditions}
                </InlineEmptyText>
            )}
        </Stack>
    )
}
